import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import ProgressBar from '../components/ProgressBar';

const SummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, resetBooking } = useBooking();
  const [isBooking, setIsBooking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleEdit = (step: number) => {
    const routes = ['/start', '/transport-booking', '/stay-booking', '/workspace-booking', '/community'];
    navigate(routes[step - 1]);
  };

  const handleBook = async () => {
    setIsBooking(true);
    
    // 模擬 API 調用
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsBooking(false);
    setShowSuccess(true);
    
    // 3秒後自動跳轉到首頁
    setTimeout(() => {
      resetBooking();
      navigate('/');
    }, 3000);
  };

  const handleReset = () => {
    resetBooking();
    navigate('/start');
  };

  // 計算總金額
  const calculateTotal = () => {
    let total = 0;

    // 交通費用
    if (state.transportOption) {
      const transportPrice = parseInt(state.transportOption.price.replace(/[^0-9]/g, ''));
      total += transportPrice;
    }

    // 住宿費用
    if (state.stayOption) {
      const stayPrice = parseInt(state.stayOption.price.replace(/[^0-9]/g, ''));
      total += stayPrice;
    }

    // 工作空間費用
    if (state.workspaceOption) {
      const workspacePrice = parseInt(state.workspaceOption.price.replace(/[^0-9]/g, ''));
      total += workspacePrice;
    }

    // 社群活動費用（免費的不計入）
    state.communityOptions.forEach(option => {
      if (!option.free) {
        const communityPrice = parseInt(option.price.replace(/[^0-9]/g, ''));
        total += communityPrice;
      }
    });

    // 文化體驗費用
    state.cultureExperiences.forEach(option => {
      const culturePrice = parseInt(option.price.replace(/[^0-9]/g, ''));
      total += culturePrice;
    });

    return total;
  };

  const totalAmount = calculateTotal();

  // 預約成功動畫組件
  const SuccessAnimation = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-12 max-w-md mx-4 text-center animate-bounce-in">
        {/* 成功圖標動畫 */}
        <div className="relative mb-8">
          <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <div className="text-4xl animate-bounce">🎉</div>
          </div>
          
          {/* 圓圈動畫 */}
          <div className="absolute inset-0 border-4 border-green-300 rounded-full animate-ping"></div>
          <div className="absolute inset-2 border-4 border-green-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute inset-4 border-4 border-green-500 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        </div>

        {/* 成功文字 */}
        <h2 className="text-3xl font-bold text-gray-800 mb-4 animate-fade-in">
          預約成功！
        </h2>
        <p className="text-gray-600 mb-6 animate-fade-in" style={{animationDelay: '0.3s'}}>
          我們已收到您的預約申請，將盡快與您聯繫確認詳細資訊
        </p>

        {/* 預約編號 */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 animate-fade-in" style={{animationDelay: '0.6s'}}>
          <p className="text-sm text-gray-500 mb-1">預約編號</p>
          <p className="text-lg font-mono font-bold text-gray-800">
            {`HL${Date.now().toString().slice(-8)}`}
          </p>
        </div>

        {/* 倒計時 */}
        <div className="text-sm text-gray-500 animate-fade-in" style={{animationDelay: '0.9s'}}>
          <span className="inline-block animate-pulse">⏰</span> 3秒後自動跳轉到首頁
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <ProgressBar currentStep={7} />
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            預約總覽
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            確認您的選擇，完成預約流程
          </p>
        </div>

        <div className="space-y-6">
          {/* 身分 */}
          <div className="bg-white rounded-2xl shadow-soft p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  旅客身分
                </h3>
                <p className="text-gray-600">
                  {state.userType === 'local' ? '台灣旅客' : '外國旅客'}
                </p>
              </div>
              <button
                onClick={() => handleEdit(1)}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
              >
                修改
              </button>
            </div>
          </div>

          {/* 交通 */}
          {state.transportOption && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    交通方式
                  </h3>
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-3">{state.transportOption.icon}</span>
                    <div>
                      <p className="text-gray-800 font-medium">{state.transportOption.title}</p>
                      <p className="text-gray-600 text-sm">{state.transportOption.description}</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    費用：{state.transportOption.price} | 時間：{state.transportOption.duration}
                  </p>
                </div>
                <button
                  onClick={() => handleEdit(2)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
                >
                  修改
                </button>
              </div>
            </div>
          )}

          {/* 住宿 */}
          {state.stayOption && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    住宿選擇
                  </h3>
                  <div className="flex items-center mb-2">
                    <img
                      src={state.stayOption.image}
                      alt={state.stayOption.name}
                      className="w-16 h-16 object-cover rounded-lg mr-4"
                    />
                    <div>
                      <p className="text-gray-800 font-medium">{state.stayOption.name}</p>
                      <p className="text-gray-600 text-sm">{state.stayOption.type}</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    價格：{state.stayOption.price}
                  </p>
                </div>
                <button
                  onClick={() => handleEdit(3)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
                >
                  修改
                </button>
              </div>
            </div>
          )}

          {/* 工作空間 */}
          {state.workspaceOption && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    工作空間
                  </h3>
                  <div className="flex items-center mb-2">
                    <img
                      src={state.workspaceOption.image}
                      alt={state.workspaceOption.name}
                      className="w-16 h-16 object-cover rounded-lg mr-4"
                    />
                    <div>
                      <p className="text-gray-800 font-medium">{state.workspaceOption.name}</p>
                      <p className="text-gray-600 text-sm">{state.workspaceOption.description}</p>
                    </div>
                  </div>
                  <p className="text-gray-600">
                    價格：{state.workspaceOption.price} | Wi-Fi：{state.workspaceOption.features.wifi}
                  </p>
                </div>
                <button
                  onClick={() => handleEdit(4)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
                >
                  修改
                </button>
              </div>
            </div>
          )}

          {/* 社群活動 */}
          {state.communityOptions.length > 0 && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    社群活動 ({state.communityOptions.length} 個)
                  </h3>
                  <div className="space-y-2">
                    {state.communityOptions.map((option, index) => (
                      <div key={index} className="flex items-center">
                        <span className="text-2xl mr-3">{option.icon}</span>
                        <div>
                          <p className="text-gray-800 font-medium">{option.name}</p>
                          <p className="text-gray-600 text-sm">{option.free ? '免費' : option.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(5)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
                >
                  修改
                </button>
              </div>
            </div>
          )}

          {/* 文化體驗 */}
          {state.cultureExperiences.length > 0 && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    文化體驗 ({state.cultureExperiences.length} 個)
                  </h3>
                  <div className="space-y-2">
                    {state.cultureExperiences.map((option, index) => (
                      <div key={index} className="flex items-center">
                        <span className="text-2xl mr-3">{option.icon}</span>
                        <div>
                          <p className="text-gray-800 font-medium">{option.name}</p>
                          <p className="text-gray-600 text-sm">{option.price} • {option.duration}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(6)}
                  className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-300"
                >
                  修改
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 總金額顯示 */}
        {totalAmount > 0 && (
          <div className="text-right mb-8 mt-12">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              預約總金額
            </h3>
            <p className="text-3xl font-bold text-blue-600">
              NT$ {totalAmount.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              已含稅金
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12">
          <button
            onClick={handleBook}
            disabled={isBooking}
            className={`w-full sm:w-auto font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-large text-lg ${
              isBooking
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
            }`}
          >
            {isBooking ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                處理中...
              </div>
            ) : (
              '一鍵預約'
            )}
          </button>
          
          <button
            onClick={handleReset}
            disabled={isBooking}
            className={`w-full sm:w-auto font-semibold py-4 px-8 rounded-2xl transition-all duration-300 ${
              isBooking
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            重新開始
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            預約完成後，我們會透過 email 或電話與您聯繫確認詳細資訊
          </p>
        </div>
      </div>

      {/* 預約成功動畫 */}
      {showSuccess && <SuccessAnimation />}
    </div>
  );
};

export default SummaryPage; 